import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  Box
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FiEdit } from 'react-icons/fi';
import { Document, pdfjs, Page as Pages } from 'react-pdf';
import { useDispatch } from 'react-redux';
import { BiCloudDownload } from 'react-icons/bi';

// material icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DescriptionIcon from '@mui/icons-material/Description';
import QrCode2Icon from '@mui/icons-material/QrCode2';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Label from '../../components/Label';
import { PreviewImageApi } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import { getOnboardingView } from '../../store/Slice/Onboarding/OnboardingSlice';
import { normalizeValue } from '../../utils/CommonFunction';
import { NOT_AVAILABLE } from '../Global/Constants';
import cancelChequeDummy from '../OnBoarding/dummy/cancelChequeDummy.png';
import photoDummy from '../OnBoarding/dummy/photoDummy.jpg';
import aadharFrontDummy from '../OnBoarding/dummy/aadharFrontDummy.jpg';
import panCardDummy from '../OnBoarding/dummy/panCardDummy.jpg';
import scan from '../OnBoarding/dummy/scan.png';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function InfoTile({ icon, label, value }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        height: '100%',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: '#fff',
          borderColor: '#cbd5e1',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          bgcolor: 'rgba(19, 81, 157, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#13519d',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'block',
            mb: 0.3
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: '#1e293b',
            wordBreak: 'break-word'
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function ViewPartner() {
  const [pageNumber, setPageNumber] = useState(1);
  const [chequePreview] = useState(cancelChequeDummy);
  const [photoPreview] = useState(photoDummy);
  const [aadharFrontPreview] = useState(aadharFrontDummy);
  const [panPreview] = useState(panCardDummy);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [previewFileType, setPreviewFileType] = useState('image');
  const [docBlobUrls, setDocBlobUrls] = useState({});
  const [qrAppBlobUrl, setQrAppBlobUrl] = useState(null);
  const [qrOnboardBlobUrl, setQrOnboardBlobUrl] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId: paramRowId } = useParams();
  const stateRowId = location?.state?.rowId;
  const partnerId =
    (typeof stateRowId === 'object' ? stateRowId?.partnerId : stateRowId) || paramRowId;

  const { LoginData } = useLoginSelector();
  const { OnboardingViewData: reviewData } = useOnboardingSelector();
  const { AccessLevelConfig } = useGlobalSelector();
  const canEditPartner = AccessLevelConfig?.accessPagesMapping
    ?.find((accessPageMap) => accessPageMap?.pageCode === 'ONBOARDING_PAGE')
    ?.accessSubPagesMappings?.some(
      (pageElement) => pageElement?.subPageCode === 'ADD_NEW' && pageElement?.isEnable === true
    );

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPageNumber(1);
  }, []);

  useEffect(() => {
    const credential = {
      companyId: LoginData?.companyId || '',
      partnerId: partnerId
    };
    dispatch(getOnboardingView(credential));
  }, [LoginData, partnerId, dispatch]);

  useEffect(() => {
    if (reviewData?.qrCodeApplicationUrl) {
      const fetchAppQr = async () => {
        const endpoint = reviewData.qrCodeApplicationUrl.startsWith('/')
          ? reviewData.qrCodeApplicationUrl.substring(1)
          : reviewData.qrCodeApplicationUrl;
        const blob = await dispatch(PreviewImageApi(endpoint, null, true));
        if (blob) {
          setQrAppBlobUrl(URL.createObjectURL(blob));
        }
      };
      fetchAppQr();
    }
    if (reviewData?.qrCodeOnboardUrl) {
      const fetchOnboardQr = async () => {
        const endpoint = reviewData.qrCodeOnboardUrl.startsWith('/')
          ? reviewData.qrCodeOnboardUrl.substring(1)
          : reviewData.qrCodeOnboardUrl;
        const blob = await dispatch(PreviewImageApi(endpoint, null, true));
        if (blob) {
          setQrOnboardBlobUrl(URL.createObjectURL(blob));
        }
      };
      fetchOnboardQr();
    }
    if (reviewData?.partnerDocuments?.length > 0) {
      reviewData.partnerDocuments.forEach(async (doc) => {
        if (doc?.documentUrl) {
          const endpoint = doc.documentUrl.startsWith('/')
            ? doc.documentUrl.substring(1)
            : doc.documentUrl;
          const blob = await dispatch(PreviewImageApi(endpoint, null, true));
          if (blob) {
            setDocBlobUrls((prev) => ({
              ...prev,
              [doc.partnerDocumentId]: {
                url: URL.createObjectURL(blob),
                type: blob.type
              }
            }));
          }
        }
      });
    }
  }, [reviewData, dispatch]);

  const isIndividual = reviewData?.typeId === 66;

  const infoFields = useMemo(
    () => [
      {
        icon: <BadgeOutlinedIcon fontSize="small" />,
        label: 'Partner Type',
        value: normalizeValue(reviewData?.partnerType?.typeCode, NOT_AVAILABLE)
      },
      {
        icon: isIndividual ? (
          <PersonOutlineIcon fontSize="small" />
        ) : (
          <CorporateFareIcon fontSize="small" />
        ),
        label: isIndividual ? 'Partner Name' : 'Company Name',
        value: normalizeValue(reviewData?.partnerName, NOT_AVAILABLE)
      },
      {
        icon: <PersonOutlineIcon fontSize="small" />,
        label: isIndividual ? "Father's Name" : 'Authorised Person Name',
        value: normalizeValue(
          isIndividual ? reviewData?.fathersName : reviewData?.authorisedPersonName,
          NOT_AVAILABLE
        )
      },
      {
        icon: <CalendarTodayIcon fontSize="small" />,
        label: isIndividual ? 'Date of Birth' : 'Date of Incorporation',
        value: normalizeValue(reviewData?.dob, NOT_AVAILABLE)
      },
      {
        icon: <PhoneAndroidIcon fontSize="small" />,
        label: 'Mobile Phone',
        value: normalizeValue(reviewData?.mobile, NOT_AVAILABLE)
      },
      {
        icon: <EmailOutlinedIcon fontSize="small" />,
        label: 'Email Address',
        value: normalizeValue(reviewData?.emailAddress, NOT_AVAILABLE)
      },
      {
        icon: <LocationOnOutlinedIcon fontSize="small" />,
        label: 'Address',
        value: normalizeValue(reviewData?.address, NOT_AVAILABLE)
      },
      {
        icon: <LocationCityIcon fontSize="small" />,
        label: 'City',
        value: normalizeValue(reviewData?.cityEntity?.cityName, NOT_AVAILABLE)
      },
      {
        icon: <ApartmentIcon fontSize="small" />,
        label: 'State',
        value: normalizeValue(reviewData?.stateEntity?.stateName, NOT_AVAILABLE)
      }
    ],
    [reviewData, isIndividual]
  );

  async function handlePreviewDoc(partnerDocumentId) {
    const { partnerId, companyId } = reviewData;

    const blob = await dispatch(
      PreviewImageApi(`pdoc/${companyId}/${partnerId}/${partnerDocumentId}`, null, true)
    );

    if (!blob) return;

    setPreviewFileType(blob.type === 'application/pdf' ? 'pdf' : 'image');
    const fileURL = URL.createObjectURL(blob);
    setPreviewUrl(fileURL);
    setOpenPreview(true);
  }

  async function handleDownloadDoc(partnerDocumentId, fileName = 'document') {
    const { partnerId, companyId } = reviewData;
    const blob = await dispatch(
      PreviewImageApi(`pdoc/${companyId}/${partnerId}/${partnerDocumentId}`, null, true)
    );

    if (!blob) return;

    const extension = blob.type === 'application/pdf' ? 'pdf' : 'png';
    const fileURL = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', `${fileName}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(fileURL);
  }

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleDoubleClick = () => {
    if (zoomLevel === 1) {
      setZoomLevel(2);
    } else {
      setZoomLevel(1);
    }
  };

  return (
    <Page title="Partner Management System || Partner Details">
      <Container maxWidth="xl">
        {reviewData && (
          <Box pt={3} pb={4} mb={4}>
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className="pageHeading"
              sx={{ mb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/partners')}
                  sx={{
                    borderRadius: '8px',
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2,
                    py: 0.8,
                    bgcolor: '#fff',
                    '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                  }}
                >
                  Back
                </Button>
                <PageTitle info="Partner Details" />
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                {canEditPartner && (
                  <Button
                    variant="contained"
                    startIcon={<FiEdit size={16} />}
                    onClick={() => navigate(`/partners/edit/${partnerId}`)}
                    sx={{
                      bgcolor: '#13519d',
                      color: '#fff',
                      borderRadius: '8px',
                      px: 2.5,
                      py: 0.8,
                      fontSize: 13,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 2px 8px rgba(19, 81, 157, 0.25)',
                      '&:hover': { bgcolor: '#0d3b75' }
                    }}
                  >
                    Edit Partner
                  </Button>
                )}
                <Box>
                  <BrudCrumbs />
                </Box>
              </Stack>
            </Stack>

            <Stack spacing={3}>
              {/* Card 1: Partner Details */}
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        bgcolor: 'rgba(19, 81, 157, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#13519d'
                      }}
                    >
                      <PersonOutlineIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      Partner Profile &amp; Basic Info
                    </Typography>
                  </Stack>

                  <Label
                    variant="ghost"
                    color="primary"
                    sx={{
                      fontWeight: 600,
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.5
                    }}
                  >
                    {reviewData?.partnerType?.typeCode || 'Partner'}
                  </Label>
                </Stack>

                <Grid container spacing={2.5}>
                  {infoFields?.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <InfoTile icon={item.icon} label={item.label} value={item.value} />
                    </Grid>
                  ))}
                </Grid>
              </Card>

              {/* Card 2: Document Verification */}
              <Card
                sx={{
                  p: 3.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '10px',
                      bgcolor: 'rgba(2, 132, 199, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0284c7'
                    }}
                  >
                    <DescriptionIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    Document Verification
                  </Typography>
                </Stack>

                <Grid container spacing={3}>
                  {(() => {
                    const requiredDocs = [
                      { typeCode: 'USER IMAGE', label: 'User Image', dummy: photoPreview },
                      {
                        typeCode: 'CHEQUE',
                        label: 'Cancelled Cheque',
                        dummy: chequePreview
                      },
                      {
                        typeCode: 'AADHAR CARD',
                        label: 'Aadhar Card',
                        dummy: aadharFrontPreview
                      },
                      { typeCode: 'PAN CARD', label: 'PAN Card', dummy: panPreview }
                    ];

                    return requiredDocs.map((config, index) => {
                      const uploadedDoc = reviewData?.partnerDocuments?.find(
                        (d) => d?.documentType?.typeCode === config.typeCode
                      );

                      const fetchedDoc = uploadedDoc
                        ? docBlobUrls[uploadedDoc.partnerDocumentId]
                        : null;
                      const isPdf = fetchedDoc?.type === 'application/pdf';
                      const imgSrc = fetchedDoc && !isPdf ? fetchedDoc.url : config.dummy;

                      return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              bgcolor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              textAlign: 'center',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  mb: 0.5
                                }}
                              >
                                {config.label}
                              </Typography>
                              <Label
                                variant="ghost"
                                color={uploadedDoc ? 'success' : 'default'}
                                sx={{ fontSize: 11, fontWeight: 600, borderRadius: '4px' }}
                              >
                                {uploadedDoc ? 'Uploaded' : 'Pending'}
                              </Label>
                            </Box>

                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: 160,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                bgcolor: '#fff',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: uploadedDoc ? 'pointer' : 'default',
                                '&:hover .docOverlay': {
                                  opacity: uploadedDoc ? 1 : 0
                                }
                              }}
                              onClick={() => {
                                if (fetchedDoc) {
                                  setPreviewFileType(isPdf ? 'pdf' : 'image');
                                  setPreviewUrl(fetchedDoc.url);
                                  setOpenPreview(true);
                                } else if (uploadedDoc) {
                                  handlePreviewDoc(uploadedDoc.partnerDocumentId);
                                }
                              }}
                            >
                              <Box
                                component="img"
                                src={imgSrc}
                                alt={config.label}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  opacity: uploadedDoc ? 1 : 0.45
                                }}
                              />

                              {uploadedDoc && (
                                <Box
                                  className="docOverlay"
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    bgcolor: 'rgba(15, 23, 42, 0.65)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease'
                                  }}
                                >
                                  <Tooltip title="Preview">
                                    <IconButton
                                      size="small"
                                      sx={{
                                        bgcolor: '#fff',
                                        color: '#13519d',
                                        '&:hover': { bgcolor: '#f8fafc' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (fetchedDoc) {
                                          setPreviewFileType(isPdf ? 'pdf' : 'image');
                                          setPreviewUrl(fetchedDoc.url);
                                          setOpenPreview(true);
                                        } else {
                                          handlePreviewDoc(uploadedDoc.partnerDocumentId);
                                        }
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      sx={{
                                        bgcolor: '#fff',
                                        color: '#10b981',
                                        '&:hover': { bgcolor: '#f8fafc' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (fetchedDoc) {
                                          const extension = isPdf ? 'pdf' : 'png';
                                          const link = document.createElement('a');
                                          link.href = fetchedDoc.url;
                                          link.setAttribute(
                                            'download',
                                            `${config.label}.${extension}`
                                          );
                                          document.body.appendChild(link);
                                          link.click();
                                          link.parentNode.removeChild(link);
                                        } else {
                                          handleDownloadDoc(
                                            uploadedDoc.partnerDocumentId,
                                            config.label
                                          );
                                        }
                                      }}
                                    >
                                      <BiCloudDownload size={18} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      );
                    });
                  })()}
                </Grid>
              </Card>

              {/* Card 3: QR Code Documents */}
              {(reviewData?.qrCodeApplicationUrl || reviewData?.qrCodeOnboardUrl) && (
                <Card
                  sx={{
                    p: 3.5,
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        bgcolor: 'rgba(99, 102, 241, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6366f1'
                      }}
                    >
                      <QrCode2Icon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      QR Code Documents
                    </Typography>
                  </Stack>

                  <Grid container spacing={3}>
                    {reviewData?.qrCodeApplicationUrl && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#1e293b',
                              mb: 1.5
                            }}
                          >
                            QR Code Application
                          </Typography>
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: 160,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              bgcolor: '#fff',
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: qrAppBlobUrl ? 'pointer' : 'default',
                              '&:hover .qrOverlay': {
                                opacity: qrAppBlobUrl ? 1 : 0
                              }
                            }}
                            onClick={() => {
                              if (qrAppBlobUrl) {
                                setPreviewFileType('image');
                                setPreviewUrl(qrAppBlobUrl);
                                setOpenPreview(true);
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={qrAppBlobUrl || scan}
                              alt="QR Code Application"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                            {qrAppBlobUrl && (
                              <Box
                                className="qrOverlay"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  bgcolor: 'rgba(15, 23, 42, 0.65)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1.5,
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease'
                                }}
                              >
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff',
                                      color: '#13519d',
                                      '&:hover': { bgcolor: '#f8fafc' }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewFileType('image');
                                      setPreviewUrl(qrAppBlobUrl);
                                      setOpenPreview(true);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff',
                                      color: '#10b981',
                                      '&:hover': { bgcolor: '#f8fafc' }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const link = document.createElement('a');
                                      link.href = qrAppBlobUrl;
                                      link.setAttribute('download', 'QRCodeApplication.png');
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <BiCloudDownload size={18} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {reviewData?.qrCodeOnboardUrl && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#1e293b',
                              mb: 1.5
                            }}
                          >
                            QR Code Onboard
                          </Typography>
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: 160,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              bgcolor: '#fff',
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: qrOnboardBlobUrl ? 'pointer' : 'default',
                              '&:hover .qrOverlay': {
                                opacity: qrOnboardBlobUrl ? 1 : 0
                              }
                            }}
                            onClick={() => {
                              if (qrOnboardBlobUrl) {
                                setPreviewFileType('image');
                                setPreviewUrl(qrOnboardBlobUrl);
                                setOpenPreview(true);
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={qrOnboardBlobUrl || scan}
                              alt="QR Code Onboard"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                            {qrOnboardBlobUrl && (
                              <Box
                                className="qrOverlay"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  bgcolor: 'rgba(15, 23, 42, 0.65)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1.5,
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease'
                                }}
                              >
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff',
                                      color: '#13519d',
                                      '&:hover': { bgcolor: '#f8fafc' }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewFileType('image');
                                      setPreviewUrl(qrOnboardBlobUrl);
                                      setOpenPreview(true);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff',
                                      color: '#10b981',
                                      '&:hover': { bgcolor: '#f8fafc' }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const link = document.createElement('a');
                                      link.href = qrOnboardBlobUrl;
                                      link.setAttribute('download', 'QRCodeOnboard.png');
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <BiCloudDownload size={18} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              )}
            </Stack>
          </Box>
        )}
      </Container>

      {/* Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          handleResetZoom();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: 'rgba(19, 81, 157, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#13519d'
              }}
            >
              <VisibilityIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Document Preview
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Zoom In">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.8
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                minWidth: 44,
                textAlign: 'center',
                fontWeight: 600,
                color: '#475569'
              }}
            >
              {Math.round(zoomLevel * 100)}%
            </Typography>
            <Tooltip title="Zoom Out">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 1}
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.8
                  }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => {
                setOpenPreview(false);
                handleResetZoom();
              }}
              sx={{
                color: '#64748b',
                ml: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#f8fafc',
            overflow: 'auto',
            minHeight: 350
          }}
        >
          {previewUrl && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              {previewFileType === 'pdf' ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    p: 2,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'auto',
                    maxHeight: '70vh'
                  }}
                >
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div>Loading PDF...</div>}
                  >
                    <Pages
                      pageNumber={pageNumber}
                      scale={zoomLevel}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  onDoubleClick={handleDoubleClick}
                  sx={{
                    width: '100%',
                    maxHeight: 520,
                    objectFit: 'contain',
                    transform: `scale(${zoomLevel})`,
                    transition: 'transform 0.3s ease-in-out',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: '8px'
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            gap: 1
          }}
        >
          <Button
            onClick={handleResetZoom}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: '8px',
              borderColor: '#cbd5e1',
              color: '#64748b',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 2,
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
            }}
          >
            Reset Zoom
          </Button>
          <Button
            onClick={() => {
              setOpenPreview(false);
              handleResetZoom();
            }}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '8px',
              bgcolor: '#13519d',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              '&:hover': { bgcolor: '#0d3b75' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
export default ViewPartner;
